import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isCancel } from 'axios';
import { errorStatus, extractApiErrorMessage, isClientError } from '@/api/errors';
import { notifyError } from './toastBridge';

// Statuses with dedicated UI handling that should NOT raise a toast:
//   304 ETag cache hit · 401 login bounce · 403 hide/disable control · 404 feature-off/out-of-scope
const SILENT_STATUSES = new Set([304, 401, 403, 404]);

/** A request we aborted ourselves (its page unmounted) — not a failure the user needs to see. */
function isAbort(error: unknown): boolean {
  return isCancel(error) || (error instanceof DOMException && error.name === 'AbortError');
}

export function shouldToast(error: unknown): boolean {
  if (isAbort(error)) return false;
  const status = errorStatus(error);
  if (status != null && SILENT_STATUSES.has(status)) return false;
  return true;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (shouldToast(error)) {
        notifyError(extractApiErrorMessage(error));
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (shouldToast(error)) {
        notifyError(extractApiErrorMessage(error));
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 600_000,
      retry: (failureCount, error) => {
        // Never retry validation/permission/not-found/conflict — surface now.
        if (isClientError(error) || isAbort(error)) return false;
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
